/**
 * Inventory Management React Query Hooks
 * 
 * Provides React Query hooks for inventory operations including:
 * - Fetching inventory items with filtering
 * - Updating inventory levels and locations
 * - Inventory statistics and stock metrics
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useInventory
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryService, api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches inventory items with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering inventory
 * @param {string} [filters.warehouse_id] - Filter by warehouse
 * @param {string} [filters.product_id] - Filter by product
 * @param {string} [filters.location] - Filter by storage location
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.limit] - Items per page
 * @returns {Object} React Query result with inventory data
 * @property {Object} data - Inventory response object
 * @property {Array} data.items - Array of inventory records
 * @property {number} data.total - Total number of inventory items
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useInventory = (filters = {}) => {
  return useQuery({
    queryKey: ['inventory', filters],
    queryFn: () => inventoryService.getInventory(filters),
  });
};

/**
 * Fetches a single inventory item by ID
 * 
 * Query is disabled if itemId is not provided
 * 
 * @param {string} itemId - Inventory item ID
 * @returns {Object} React Query result with inventory item data
 * @property {Object} data - Inventory item record with full details
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useInventoryItem = (itemId) => {
  return useQuery({
    queryKey: ['inventory', itemId],
    queryFn: () => inventoryService.getInventoryItem(itemId),
    enabled: !!itemId,
  });
};

/**
 * Mutation hook to create a new inventory item
 * 
 * Automatically invalidates inventory list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createInventory = useCreateInventory();
 * createInventory.mutate({ 
 *   sku: 'SKU-001',
 *   name: 'Product Name',
 *   quantity: 100,
 *   location: 'A-01-01'
 * });
 */
export const useCreateInventory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(API_ENDPOINTS.INVENTORY, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Mutation hook to update inventory item
 * 
 * Automatically invalidates both the specific item and inventory list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateInventory = useUpdateInventory();
 * updateInventory.mutate({ 
 *   itemId: '123', 
 *   data: { quantity: 100, location: 'A-01-01' } 
 * });
 */
export const useUpdateInventory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ itemId, data }) => inventoryService.updateInventory(itemId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Mutation hook to delete an inventory item
 * 
 * Automatically invalidates inventory list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteInventory = useDeleteInventory();
 * deleteInventory.mutate('item-123');
 */
export const useDeleteInventory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (itemId) => {
      const response = await api.delete(`${API_ENDPOINTS.INVENTORY}/${itemId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Fetches inventory statistics and stock metrics
 * 
 * @param {Object} params - Query parameters for filtering stats
 * @param {string} [params.warehouse_id] - Filter by warehouse
 * @param {string} [params.category] - Filter by product category
 * @returns {Object} React Query result with inventory statistics
 * @property {Object} data - Inventory statistics object
 * @property {number} data.total_items - Total number of inventory items
 * @property {number} data.low_stock_items - Number of low stock items
 * @property {number} data.out_of_stock_items - Number of out of stock items
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useInventoryStatCards = (params = {}) => {
  return useQuery({
    queryKey: ['inventory', 'stat-cards', params],
    queryFn: () => inventoryService.getStats(params),
  });
};

/**
 * Fetches cycle count status and active counts
 * 
 * @returns {Object} React Query result with cycle count status
 * @property {Object} data - Cycle count status and active counts
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCycleCountStatus = () => {
  return useQuery({
    queryKey: ['inventory', 'cycle-count', 'status'],
    queryFn: () => inventoryService.getCycleCountStatus(),
  });
};

/**
 * Mutation hook to create a new cycle count
 * 
 * Automatically invalidates cycle count queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createCount = useCreateCycleCount();
 * createCount.mutate({ 
 *   name: 'Weekly Count - Zone A',
 *   locations: ['A-01', 'A-02'],
 *   scheduled_date: '2025-11-15T08:00:00Z'
 * });
 */
export const useCreateCycleCount = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => inventoryService.createCycleCount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'cycle-count'] });
    },
  });
};

/**
 * Mutation hook to submit a cycle count entry
 * 
 * Automatically invalidates cycle count and physical count queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const submitEntry = useSubmitCycleCountEntry();
 * submitEntry.mutate({ 
 *   cycle_count_id: 'cc-123',
 *   location: 'A-01-01',
 *   product_id: 'prod-456',
 *   counted_quantity: 50,
 *   operator_id: 'op-789'
 * });
 */
export const useSubmitCycleCountEntry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => inventoryService.submitCycleCountEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'cycle-count'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'physical-count'] });
    },
  });
};

/**
 * Fetches physical count status
 * 
 * @returns {Object} React Query result with physical count status
 * @property {Object} data - Physical count status
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const usePhysicalCountStatus = () => {
  return useQuery({
    queryKey: ['inventory', 'physical-count', 'status'],
    queryFn: () => inventoryService.getPhysicalCountStatus(),
  });
};

/**
 * Hook to get dashboard KPIs
 * @returns {Object} Query result with dashboard KPIs data
 */
export const useDashboardKPIs = () => {
  
  return useQuery({
    queryKey: ['inventory', 'dashboard', 'kpis'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.INVENTORY}/dashboard/kpis`);
      return data;
    },
  });
};

/**
 * Hook to get dashboard widgets
 * @returns {Object} Query result with dashboard widgets data
 */
export const useDashboardWidgets = () => {
  
  return useQuery({
    queryKey: ['dashboard', 'widgets'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.DASHBOARD}/widgets`);
      return data;
    },
  });
};

/**
 * Hook to scan barcode
 * @returns {Object} Mutation object for scanning barcode
 */
export const useScanBarcode = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ barcode, operator_id }) => {
      const { data } = await api.post(`${API_ENDPOINTS.INVENTORY}/scan-barcode`, {
        barcode,
        operator_id
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Hook to adjust stock
 * @returns {Object} Mutation object for adjusting stock
 */
export const useAdjustStock = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ product_id, quantity, reason, operator_id }) => {
      const { data } = await api.post(`${API_ENDPOINTS.INVENTORY}/movements`, {
        product_id,
        quantity,
        reason,
        operator_id
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Mutation hook to import inventory from CSV file
 * 
 * Automatically invalidates inventory queries on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const importCSV = useImportInventoryCSV();
 * const formData = new FormData();
 * formData.append('file', file);
 * importCSV.mutate(formData);
 */
export const useImportInventoryCSV = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (formData) => {
      const response = await api.post(`${API_ENDPOINTS.INVENTORY}/import/csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Mutation hook to export inventory to CSV
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const exportCSV = useExportInventoryCSV();
 * exportCSV.mutate({ filters: { warehouse_id: 'wh-123' } });
 */
export const useExportInventoryCSV = () => {
  return useMutation({
    mutationFn: async ({ filters = {} }) => {
      const response = await api.post(
        `${API_ENDPOINTS.INVENTORY}/export/csv`,
        { filters },
        { responseType: 'blob' }
      );
      return response.data;
    },
  });
};

/**
 * Query hook to fetch inventory preview data
 * 
 * @returns {Object} React Query result with preview data
 * @property {Array} data - Preview data array
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryPreview = () => {
  
  return useQuery({
    queryKey: ['inventory', 'preview'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/preview`);
      return response.data || [];
    },
  });
};

/**
 * Mutation hook to preview import file
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const usePreviewImportFile = () => {
  
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`${API_ENDPOINTS.INVENTORY}/import/preview`, formData);
      return (data && data.preview) ? data.preview : [];
    },
  });
};

/**
 * Mutation hook to validate import data
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useValidateImportData = () => {
  
  return useMutation({
    mutationFn: async ({ data, options }) => {
      const { data: validationResult } = await api.post(`${API_ENDPOINTS.INVENTORY}/import/validate`, {
        data,
        options
      });
      return validationResult || null;
    },
  });
};

/**
 * Fetches stock visualization data
 * 
 * @param {Object} params - Query parameters for filtering stock visualization
 * @param {string} [params.warehouse_id] - Filter by warehouse
 * @returns {Object} React Query result with stock visualization data
 * @property {Object} data - Stock visualization response object
 * @property {Array} data.stocks - Array of stock records with visualization data
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useStockVisualization = (params = {}) => {
  
  return useQuery({
    queryKey: ['inventory', 'stock-visualization', params],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/stock-visualization`, { params });
      return response.data;
    },
  });
};

/**
 * Query hook to fetch inventory chart data
 * 
 * @returns {Object} React Query result with chart data
 * @property {Array} data - Chart data array
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryChartData = () => {
  
  return useQuery({
    queryKey: ['inventory', 'chart-data'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/chart-data`);
      const payload = response.data || {};
      return Array.isArray(payload.chartData) ? payload.chartData : [];
    },
  });
};

/**
 * Query hook to fetch inventory categories statistics
 * 
 * @returns {Object} React Query result with categories statistics
 * @property {Array} data - Categories statistics array
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryCategoriesStats = () => {
  
  return useQuery({
    queryKey: ['inventory', 'categories-stats'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/categories-stats`);
      const payload = response.data || {};
      return Array.isArray(payload.categories) ? payload.categories : [];
    },
  });
};

/**
 * Query hook to fetch warehouse distribution data
 * 
 * @returns {Object} React Query result with warehouse distribution data
 * @property {Array} data - Warehouse distribution array
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryWarehouseDistribution = () => {
  
  return useQuery({
    queryKey: ['inventory', 'warehouse-distribution'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/warehouse-distribution`);
      const payload = response.data || {};
      return Array.isArray(payload.warehouses) ? payload.warehouses : [];
    },
  });
};

/**
 * Query hook to fetch inventory metrics
 * 
 * @returns {Object} React Query result with inventory metrics
 * @property {Object} data - Inventory metrics object
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryMetrics = () => {
  
  return useQuery({
    queryKey: ['inventory', 'metrics'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ANALYTICS}/inventory/metrics`);
      return response.data || {};
    },
  });
};

/**
 * Query hook to fetch inventory charts data
 * 
 * @returns {Object} React Query result with inventory charts
 * @property {Object} data - Inventory charts object
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryCharts = () => {
  
  return useQuery({
    queryKey: ['inventory', 'charts'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ANALYTICS}/inventory/charts`);
      return response.data || {};
    },
  });
};

/**
 * Fetches inventory stocks for integrated dashboard
 * 
 * @returns {Object} React Query result with stocks data
 * @property {Array} data - Array of stock records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useInventoryStocks = () => {
  return useQuery({
    queryKey: ['inventory', 'stocks', 'integrated'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/stocks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = response.data;
      return Array.isArray(data) ? data : (Array.isArray(data?.stocks) ? data.stocks : []);
    },
  });
};

/**
 * Mutation hook to update inventory stock
 * 
 * Automatically invalidates stocks query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useUpdateInventoryStock = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ productId, data }) => {
      const response = await api.put(`${API_ENDPOINTS.INVENTORY}/stocks/${productId}`, data, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stocks'] });
    },
  });
};

/**
 * Query hook to fetch live inventory dashboard data
 * 
 * @returns {Object} React Query result with dashboard data
 * @property {Object} data - Dashboard data object
 * @property {boolean} isLoading - Loading state
 */
export const useLiveInventoryDashboard = () => {
  return useQuery({
    queryKey: ['inventory', 'dashboard', 'live'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/dashboard`);
      return response.data || {};
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

/**
 * Query hook to fetch inventory locations
 * 
 * @returns {Object} React Query result with locations data
 * @property {Object} data - Locations data object
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryLocations = () => {
  return useQuery({
    queryKey: ['inventory', 'locations'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/locations`);
      return response.data || {};
    },
    refetchInterval: 30000,
  });
};

/**
 * Query hook to fetch inventory movements
 * 
 * @returns {Object} React Query result with movements data
 * @property {Object} data - Movements data object
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryMovements = () => {
  return useQuery({
    queryKey: ['inventory', 'movements'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/movements`);
      return response.data || {};
    },
    refetchInterval: 30000,
  });
};

/**
 * Query hook to fetch inventory alerts
 * 
 * @returns {Object} React Query result with alerts data
 * @property {Object} data - Alerts data object
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryAlerts = () => {
  return useQuery({
    queryKey: ['inventory', 'alerts'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/alerts`);
      return response.data || {};
    },
    refetchInterval: 30000,
  });
};

/**
 * Query hook to fetch inventory replenishment data
 * 
 * @returns {Object} React Query result with replenishment data
 * @property {Object} data - Replenishment data object
 * @property {boolean} isLoading - Loading state
 */
export const useInventoryReplenishment = () => {
  return useQuery({
    queryKey: ['inventory', 'replenishment'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/replenishment`);
      return response.data || {};
    },
    refetchInterval: 30000,
  });
};

/**
 * Query hook to fetch stock movements
 * 
 * @returns {Object} React Query result with stock movements data
 * @property {Array} data - Array of stock movement records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useStockMovements = () => {
  return useQuery({
    queryKey: ['stock-movements'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.STOCK_MOVEMENTS);
      const data = response.data || {};
      return Array.isArray(data.movements) ? data.movements : (Array.isArray(data) ? data : []);
    },
  });
};

/**
 * Query hook to fetch recent inventory movements
 * 
 * Fetches recent stock movements for inventory tracking and monitoring.
 * 
 * @returns {Object} React Query result with recent movements data
 * @property {Array} data - Array of recent movement records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useRecentInventoryMovements = () => {
  return useQuery({
    queryKey: ['inventory', 'movements', 'recent'],
    queryFn: async () => {
      const token = localStorage.getItem('wms_access_token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      };
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/movements/recent`, { headers });
      const data = response.data || {};
      return Array.isArray(data.movements) ? data.movements : (Array.isArray(data) ? data : []);
    },
  });
};

/**
 * Mutation hook to move inventory item to different location
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useMoveInventoryItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemId, locationId }) => {
      const response = await api.post(`${API_ENDPOINTS.INVENTORY}/${itemId}/move`, { location_id: locationId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Mutation hook to bulk delete inventory items
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useBulkDeleteInventory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemIds }) => {
      const response = await api.post(`${API_ENDPOINTS.INVENTORY}/bulk-delete`, { item_ids: itemIds });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Mutation hook to bulk adjust stock for inventory items
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useBulkAdjustStockInventory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemIds, adjustment }) => {
      const response = await api.post(`${API_ENDPOINTS.INVENTORY}/bulk-adjust`, { 
        item_ids: itemIds,
        adjustment 
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

/**
 * Mutation hook to bulk move inventory items
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useBulkMoveInventory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemIds, locationId }) => {
      const response = await api.post(`${API_ENDPOINTS.INVENTORY}/bulk-move`, { 
        item_ids: itemIds,
        location_id: locationId 
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};
